import { Routes, Route } from '@angular/router';
import { authGuard } from './auth/auth.guard';
import { adminGuard } from './auth/admin.guard';

// Helper function to create onboarding child routes (reused for guest and authenticated)
const createOnboardingChildRoutes = (): Route[] => [
  {
    path: '',
    redirectTo: 'name',
    pathMatch: 'full'
  },
  {
    path: 'name',
    loadComponent: () => import('./onboarding/step-name/step-name.component').then(m => m.StepNameComponent)
  },
  {
    path: 'activity',
    loadComponent: () => import('./onboarding/step-activity/step-activity.component').then(m => m.StepActivityComponent)
  },
  {
    path: 'forme',
    loadComponent: () => import('./onboarding/step-forme/step-forme.component').then(m => m.StepFormeComponent)
  },
  {
    path: 'domiciliation',
    loadComponent: () => import('./onboarding/step-domiciliation/step-domiciliation.component').then(m => m.StepDomiciliationComponent)
  },
  {
    path: 'finance',
    children: [
      {
        path: '',
        redirectTo: 'capital',
        pathMatch: 'full'
      },
      {
        path: 'capital',
        loadComponent: () => import('./onboarding/financial-sub-step/step-apport/step-apport.component').then(m => m.StepApportComponent)
      },
      {
        path: 'facturation',
        loadComponent: () => import('./onboarding/financial-sub-step/step-facturation/step-facturation.component').then(m => m.StepFacturationComponent)
      },
      {
        path: 'besoins',
        loadComponent: () => import('./onboarding/financial-sub-step/step-besoins/step-besoins.component').then(m => m.StepBesoinsComponent)
      },
      {
        path: 'remunerations',
        loadComponent: () => import('./onboarding/financial-sub-step/step-remunerations/step-remunerations.component').then(m => m.StepRemunerationsComponent)
      },
      {
        path: 'mobilite',
        loadComponent: () => import('./onboarding/financial-sub-step/step-mobilite/step-mobilite.component').then(m => m.StepMobiliteComponent)
      },
      {
        path: 'espace-travail',
        loadComponent: () => import('./onboarding/financial-sub-step/step-espace-travail/step-espace-travail.component').then(m => m.StepEspaceTravailComponent)
      },
      {
        path: 'services-externes',
        loadComponent: () => import('./onboarding/financial-sub-step/step-services-externes/step-services-externes.component').then(m => m.StepServicesExternesComponent)
      },
      {
        path: 'investissements',
        loadComponent: () => import('./onboarding/financial-sub-step/step-investissements/step-investissements.component').then(m => m.StepInvestissementsComponent)
      }
    ]
  },
  {
    path: 'coordinates',
    loadComponent: () => import('./onboarding/step-coordinates/step-coordinates.component').then(m => m.StepCoordinatesComponent)
  },
  {
    path: 'payment',
    loadComponent: () => import('./onboarding/step-payment/step-payment.component').then(m => m.StepPaymentComponent)
  },
  {
    path: 'meet',
    loadComponent: () => import('./onboarding/step-meeting/step-meeting.component').then(m => m.StepMeetingComponent)
  }
];

export const routes: Routes = [
  // Root redirect to start
  {
    path: '',
    redirectTo: '/start',
    pathMatch: 'full'
  },

  // Public Routes (no authentication required)
  {
    path: 'start',
    loadComponent: () => import('./public/start/start.component').then(m => m.StartComponent)
  },

  // Onboarding parent route
  {
    path: 'onboarding',
    children: [
      // Guest Onboarding Routes
      {
        path: 'guest/:sessionId',
        loadComponent: () => import('./onboarding/onboarding-layout/onboarding-layout.component').then(m => m.OnboardingLayoutComponent),
        children: createOnboardingChildRoutes()
      },

      // Authenticated Onboarding Routes
      {
        path: 'authenticated/:sessionId',
        loadComponent: () => import('./onboarding/onboarding-layout/onboarding-layout.component').then(m => m.OnboardingLayoutComponent),
        children: createOnboardingChildRoutes()
      },

      // Admin Read-Only Onboarding Routes
      {
        path: 'admin/:sessionId',
        loadComponent: () => import('./onboarding/onboarding-layout/onboarding-layout.component').then(m => m.OnboardingLayoutComponent),
        data: { readOnly: true },
        children: createOnboardingChildRoutes()
      },

      // Legacy route redirect to guest (for backward compatibility)
      {
        path: ':sessionId',
        redirectTo: 'guest/:sessionId',
        pathMatch: 'full'
      }
    ]
  },

  // Authentication Routes (guest only - redirects authenticated users)
  {
    path: 'auth',
    canActivate: [],
    children: [
      {
        path: '',
        redirectTo: 'login',
        pathMatch: 'full'
      },
      {
        path: 'login',
        loadComponent: () => import('./auth/login/login.component').then(m => m.LoginComponent)
      },
      {
        path: 'register',
        loadComponent: () => import('./auth/register/register.component').then(m => m.RegisterComponent)
      },
      {
        path: 'forgot-password',
        loadComponent: () => import('./auth/forgot-password/forgot-password.component').then(m => m.ForgotPasswordComponent)
      }
    ]
  },

  // User Routes (authentication required)
  {
    path: 'user',
    canActivate: [authGuard],
    loadComponent: () => import('./user/layout/layout.component').then(m => m.LayoutComponent),
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full'
      },
      {
        path: 'companies',
        loadComponent: () => import('./user/companies/companies.component').then(m => m.CompaniesComponent)
      },
      {
        path: 'company/:id',
        loadComponent: () => import('./user/company/company.component').then(m => m.CompanyComponent)
      },
      {
        path: 'company/:id/governance',
        loadComponent: () => import('./user/company/governance-edit/governance-edit.component').then(m => m.GovernanceEditComponent)
      },
      {
        path: 'profile',
        loadComponent: () => import('./user/profile/profile.component').then(m => m.ProfileComponent)
      },
      {
        path: 'support',
        loadComponent: () => import('./user/support/support.component').then(m => m.SupportComponent)
      }
      // Add more user routes here as needed
    ]
  },

  // Admin Routes (admin authentication required)
  {
    path: 'admin',
    canActivate: [adminGuard],
    loadComponent: () => import('./admin/layout/layout.component').then(m => m.LayoutComponent),
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full'
      },
      {
        path: 'dashboard',
        loadComponent: () => import('./admin/dashboard/dashboard.component').then(m => m.DashboardComponent)
      },
      {
        path: 'users',
        loadComponent: () => import('./admin/users/users.component').then(m => m.UsersComponent)
      },
      {
        path: 'companies',
        loadComponent: () => import('./admin/companies/companies.component').then(m => m.CompaniesComponent)
      },
      {
        path: 'user/:id',
        loadComponent: () => import('./admin/user/user.component').then(m => m.UserComponent)
      },
      {
        path: 'company/:id',
        loadComponent: () => import('./admin/company/company.component').then(m => m.CompanyComponent)
      },
      {
        path: 'internal-notifications',
        loadComponent: () => import('./admin/internal-notifications/internal-notifications.component').then(m => m.InternalNotificationsComponent)
      },
      {
        path: 'settings',
        loadComponent: () => import('./admin/settings/settings.component').then(m => m.SettingsComponent)
      },
      {
        path: 'settings/emails',
        loadComponent: () => import('./admin/settings/emails/emails.component').then(m => m.EmailsComponent)
      }
    ]
  },

  // Wildcard route - redirect to start
  {
    path: '**',
    redirectTo: '/start'
  }
];
